import { useCallback, useEffect, useRef } from "react";

interface UseTosScrollDetectionProps {
    currentStep: number;
    completedSteps: Set<number>;
    threshold: number;
    onStepCompleted: (step: number) => void;
}

interface UseTosScrollDetectionReturn {
    contentRef: React.RefObject<HTMLDivElement>;
}

export const useTosScrollDetection = ({
    currentStep,
    completedSteps,
    threshold,
    onStepCompleted,
}: UseTosScrollDetectionProps): UseTosScrollDetectionReturn => {
    const contentRef = useRef<HTMLDivElement>(null);

    // Scroll detection logic
    const handleScroll = useCallback(() => {
        if (!contentRef.current) {
            return;
        }

        const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
        const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

        if (scrollPercentage >= threshold && !completedSteps.has(currentStep)) {
            onStepCompleted(currentStep);
        }
    }, [currentStep, completedSteps, threshold, onStepCompleted]);

    // Add scroll listener
    useEffect(() => {
        const contentElement = contentRef.current;
        if (contentElement) {
            contentElement.addEventListener("scroll", handleScroll);
            return () => {
                contentElement.removeEventListener("scroll", handleScroll);
            };
        }
        return;
    }, [handleScroll]);

    // Reset scroll position whenever step changes
    useEffect(() => {
        if (contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
    }, [currentStep]);

    return {
        contentRef,
    };
};
